import React, { useState } from "react";
import {
  Eye,
  BookOpen,
  CreditCard,
  Bus,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import "./ChildMonitoring.css";

const ChildMonitoring = () => {
  const { user } = useAuth();
  const [selectedChild] = useState("Emma Wilson"); // In real app, this would be dynamic
  const [activeTab, setActiveTab] = useState("overview");

  // Mock data for child monitoring
  const childData = {
    name: "Emma Wilson",
    grade: "Grade 10",
    studentId: "STU001",
    avatar: "👩‍🎓",
    currentLocation: "Library",
    lastSeen: "2 minutes ago",
    status: "active",
  };

  const libraryActivity = [
    {
      id: 1,
      bookTitle: "Advanced Mathematics",
      action: "borrowed",
      date: "2024-01-15",
      dueDate: "2024-02-15",
      status: "active",
    },
    {
      id: 2,
      bookTitle: "Physics Fundamentals",
      action: "returned",
      date: "2024-01-10",
      status: "completed",
    },
    {
      id: 3,
      bookTitle: "World History",
      action: "requested",
      date: "2024-01-12",
      status: "pending",
    },
  ];

  const cafeteriaActivity = [
    {
      id: 1,
      item: "Chicken Burger",
      amount: 8.5,
      date: "2024-01-15",
      time: "12:30 PM",
      location: "Main Cafeteria",
    },
    {
      id: 2,
      item: "Fresh Juice",
      amount: 3.0,
      date: "2024-01-15",
      time: "12:35 PM",
      location: "Main Cafeteria",
    },
    {
      id: 3,
      item: "Caesar Salad",
      amount: 6.0,
      date: "2024-01-14",
      time: "12:15 PM",
      location: "Main Cafeteria",
    },
  ];

  const transportationActivity = [
    {
      id: 1,
      route: "Route 3 - Downtown",
      action: "boarded",
      time: "7:45 AM",
      date: "2024-01-15",
      status: "completed",
    },
    {
      id: 2,
      route: "Route 3 - Downtown",
      action: "disembarked",
      time: "3:20 PM",
      date: "2024-01-15",
      status: "completed",
    },
    {
      id: 3,
      route: "Route 3 - Downtown",
      action: "delayed",
      time: "7:50 AM",
      date: "2024-01-14",
      status: "alert",
    },
  ];

  const eLibraryActivity = [
    {
      id: 1,
      title: "Advanced Mathematics Textbook",
      action: "downloaded",
      date: "2024-01-15",
      type: "ebook",
    },
    {
      id: 2,
      title: "Physics Lab Experiments",
      action: "viewed",
      date: "2024-01-14",
      type: "video",
    },
    {
      id: 3,
      title: "English Literature Audio",
      action: "downloaded",
      date: "2024-01-13",
      type: "audio",
    },
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
      case "completed":
        return <CheckCircle size={16} className="text-green-500" />;
      case "pending":
        return <Clock size={16} className="text-yellow-500" />;
      case "alert":
        return <AlertTriangle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
      case "completed":
        return "status-success";
      case "pending":
        return "status-pending";
      case "alert":
        return "status-alert";
      default:
        return "status-pending";
    }
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: TrendingUp },
    { id: "library", label: "Library", icon: BookOpen },
    { id: "cafeteria", label: "Cafeteria", icon: CreditCard },
    { id: "transportation", label: "Transportation", icon: Bus },
    { id: "e-library", label: "E-Library", icon: BookOpen },
  ];

  const renderOverview = () => (
    <div className="overview-content">
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon library">
            <BookOpen size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">
              {
                libraryActivity.filter((item) => item.status === "active")
                  .length
              }
            </span>
            <span className="stat-label">Active Borrows</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon cafeteria">
            <CreditCard size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">
              $
              {cafeteriaActivity
                .reduce((sum, item) => sum + item.amount, 0)
                .toFixed(2)}
            </span>
            <span className="stat-label">Today's Spending</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon transportation">
            <Bus size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">
              {
                transportationActivity.filter(
                  (item) => item.status === "completed"
                ).length
              }
            </span>
            <span className="stat-label">Trips Today</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon e-library">
            <BookOpen size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{eLibraryActivity.length}</span>
            <span className="stat-label">E-Library Activities</span>
          </div>
        </div>
      </div>

      <div className="recent-activity">
        <h3>Recent Activity</h3>
        <div className="activity-list">
          {[
            ...libraryActivity,
            ...cafeteriaActivity,
            ...transportationActivity,
            ...eLibraryActivity,
          ]
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .slice(0, 5)
            .map((activity, index) => (
              <div key={index} className="activity-item">
                <div className="activity-icon">
                  {activity.bookTitle && <BookOpen size={16} />}
                  {activity.item && <CreditCard size={16} />}
                  {activity.route && <Bus size={16} />}
                  {activity.title && <BookOpen size={16} />}
                </div>
                <div className="activity-content">
                  <div className="activity-title">
                    {activity.bookTitle ||
                      activity.item ||
                      activity.route ||
                      activity.title}
                  </div>
                  <div className="activity-details">
                    {activity.action} • {activity.date}
                  </div>
                </div>
                <div className="activity-status">
                  {getStatusIcon(activity.status)}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );

  const renderLibrary = () => (
    <div className="library-content">
      <div className="section-header">
        <h3>Library Activity</h3>
        <span className="section-subtitle">
          Books borrowed, returned, and requested
        </span>
      </div>
      <div className="activity-table">
        <div className="table-header">
          <div>Book Title</div>
          <div>Action</div>
          <div>Date</div>
          <div>Due Date</div>
          <div>Status</div>
        </div>
        {libraryActivity.map((activity) => (
          <div key={activity.id} className="table-row">
            <div className="book-title">{activity.bookTitle}</div>
            <div className="action">{activity.action}</div>
            <div className="date">{activity.date}</div>
            <div className="due-date">{activity.dueDate || "N/A"}</div>
            <div className={`status ${getStatusColor(activity.status)}`}>
              {getStatusIcon(activity.status)}
              <span>{activity.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCafeteria = () => (
    <div className="cafeteria-content">
      <div className="section-header">
        <h3>Cafeteria Activity</h3>
        <span className="section-subtitle">Food purchases and spending</span>
      </div>
      <div className="activity-table">
        <div className="table-header">
          <div>Item</div>
          <div>Amount</div>
          <div>Date</div>
          <div>Time</div>
          <div>Location</div>
        </div>
        {cafeteriaActivity.map((activity) => (
          <div key={activity.id} className="table-row">
            <div className="item-name">{activity.item}</div>
            <div className="amount">${activity.amount.toFixed(2)}</div>
            <div className="date">{activity.date}</div>
            <div className="time">{activity.time}</div>
            <div className="location">{activity.location}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTransportation = () => (
    <div className="transportation-content">
      <div className="section-header">
        <h3>Transportation Activity</h3>
        <span className="section-subtitle">Bus routes and travel history</span>
      </div>
      <div className="activity-table">
        <div className="table-header">
          <div>Route</div>
          <div>Action</div>
          <div>Date</div>
          <div>Time</div>
          <div>Status</div>
        </div>
        {transportationActivity.map((activity) => (
          <div key={activity.id} className="table-row">
            <div className="route-name">{activity.route}</div>
            <div className="action">{activity.action}</div>
            <div className="date">{activity.date}</div>
            <div className="time">{activity.time}</div>
            <div className={`status ${getStatusColor(activity.status)}`}>
              {getStatusIcon(activity.status)}
              <span>{activity.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderELibrary = () => (
    <div className="e-library-content">
      <div className="section-header">
        <h3>E-Library Activity</h3>
        <span className="section-subtitle">
          Digital content access and downloads
        </span>
      </div>
      <div className="activity-table">
        <div className="table-header">
          <div>Title</div>
          <div>Action</div>
          <div>Type</div>
          <div>Date</div>
        </div>
        {eLibraryActivity.map((activity) => (
          <div key={activity.id} className="table-row">
            <div className="title">{activity.title}</div>
            <div className="action">{activity.action}</div>
            <div className="type">{activity.type}</div>
            <div className="date">{activity.date}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="child-monitoring">
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <Eye size={24} />
          </div>
          <div>
            <h1>Child Monitoring</h1>
            <p>Monitor your child's school activities and progress</p>
          </div>
        </div>
        <div className="child-info">
          <div className="child-avatar">{childData.avatar}</div>
          <div className="child-details">
            <h3>{childData.name}</h3>
            <p>
              {childData.grade} • {childData.studentId}
            </p>
            <div className="child-status">
              <span className={`status-indicator ${childData.status}`}></span>
              <span>
                {childData.currentLocation} • {childData.lastSeen}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="monitoring-content">
        <div className="tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={20} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="tab-content">
          {activeTab === "overview" && renderOverview()}
          {activeTab === "library" && renderLibrary()}
          {activeTab === "cafeteria" && renderCafeteria()}
          {activeTab === "transportation" && renderTransportation()}
          {activeTab === "e-library" && renderELibrary()}
        </div>
      </div>
    </div>
  );
};

export default ChildMonitoring;
